import PropTypes from "prop-types";
import { useState, useCallback, useMemo, useEffect } from "react";
import "./index.css";

// MUI
import { Divider, Stack } from "@mui/material";

// Components
import TabActions from "./TabActions";
import TabsPanels from "./TabsPanels";
import Selectors from "./Selectors";
import { TabContext } from "@mui/lab";

const splitActions = (actions = [], max = 3) => {
	const limit = Math.ceil(actions.length / max) || 1;
	const results = Array.from(Array(limit)).map(() => []);
	let allocate = 0;
	let maxAllowed = 0;
	for (const action of actions) {
		results[allocate].push(action);
		maxAllowed++;
		if (maxAllowed >= max) {
			maxAllowed = 0;
			allocate++;
		}
	}
	return results;
};

function SelectableSelectors(props) {
	const {
		selected = 0,
		tabs = [],
		errors = [],
		actions = [],
		generalProps = null,
		tabsProps = null,
		style = null,
		handleChange = () => {},
	} = props;

	const allocatedActions = splitActions(actions);

	if (allocatedActions?.length > 1) {
		const regularActions = allocatedActions.shift();
		return (
			<Stack {...generalProps}>
				{allocatedActions.map((levelActions, key) => (
					<Stack key={`top_actions_${selected}_${key}`} direction={"row"} justifyContent={"flex-end"}>
						<TabActions actions={levelActions} />
					</Stack>
				))}
				<Stack direction={"row"} justifyContent={"space-between"}>
					<Stack>
						<Selectors tabs={tabs} errors={errors} selected={selected} tabsProps={tabsProps} handleChange={handleChange} />
					</Stack>
					<TabActions actions={regularActions} />
				</Stack>
			</Stack>
		);
	}

	return (
		<Stack {...generalProps} direction={"row"} justifyContent={"space-between"}>
			<Stack>
				<Selectors
					tabs={tabs}
					errors={errors}
					selected={selected}
					tabsProps={tabsProps}
					handleChange={handleChange}
					style={style}
				/>
			</Stack>
			<TabActions actions={actions} />
		</Stack>
	);
}
SelectableSelectors.propTypes = {
	selected: PropTypes.number,
	tabs: PropTypes.array,
	errors: PropTypes.array,
	actions: PropTypes.array,
	generalProps: PropTypes.object,
	tabsProps: PropTypes.object,
	style: PropTypes.object,
	handleChange: PropTypes.func,
};

export default function TabSelectable(props) {
	const {
		tabs = [],
		errors = [],
		content = [],
		topActions = [],
		bottomActions = [],
		tabsProps = null,
		tabBarClassName = "",
		style = null,
		dividerProps = null,
		contentProps = null,
		generalProps = {
			mx: "32px",
		},
		onTabChange = () => {},
		customComponent = null,
	} = props;

	// States
	const [value, setValue] = useState(0);

	const handleTabChange = useCallback(
		(selected) => {
			setValue(selected);
			const tabInformation = {
				index: selected,
				label: tabs?.[selected] || "",
			};
			onTabChange?.(selected, tabInformation);
		},
		[tabs, onTabChange]
	);

	const handleChange = (_, selected) => {
		handleTabChange(selected);
	};

	useMemo(() => {
		if (value >= content.length) {
			handleTabChange(Math.max(content.length - 1, 0));
		}
	}, [content, value, handleTabChange]);

	useEffect(() => {
		return () => {
			setValue(0);
		};
	}, []);
	return (
		<TabContext value={props.tabSelected ?? value}>
			<Stack>
				<Stack className={tabBarClassName}>
					<SelectableSelectors
						selected={props.tabSelected ?? value}
						tabs={tabs}
						errors={errors}
						actions={topActions}
						generalProps={generalProps}
						tabsProps={tabsProps}
						handleChange={handleChange}
						style={style}
					/>
					<Divider className="!border-disabled" {...dividerProps} />
					<TabActions generalProps={generalProps} actions={bottomActions} />
				</Stack>

				<Stack {...generalProps}>
					{customComponent}
					<TabsPanels
						content={content}
						selected={props.tabSelected ?? value}
						containerProps={contentProps}
						useMUITabPanel={props.useMUITabPanel}
					/>
				</Stack>
			</Stack>
		</TabContext>
	);
}
TabSelectable.propTypes = {
	tabs: PropTypes.array,
	errors: PropTypes.array,
	content: PropTypes.array,
	topActions: PropTypes.array,
	bottomActions: PropTypes.array,
	tabBarClassName: PropTypes.string,
	tabsProps: PropTypes.object,
	style: PropTypes.object,
	dividerProps: PropTypes.object,
	contentProps: PropTypes.object,
	generalProps: PropTypes.object,
	onTabChange: PropTypes.func,
	tabSelected: PropTypes.number,
	useMUITabPanel: PropTypes.bool,
	customComponent: PropTypes.element,
};
